import React from "react";
import Button from "./Button";

/**
 * PageHeader — Ocean Blue Design System
 *
 * Props:
 *  title       : string | ReactNode — main heading
 *  subtitle    : string             — supporting description
 *  actions     : ReactNode          — right-side action buttons slot
 *  breadcrumbs : Array<{ label, href?, onClick? }> — navigation trail
 *  backAction  : { label, onClick } — back chevron button
 *  badge       : ReactNode          — status badge beside title
 *  bordered    : boolean            — bottom border separator (default true)
 */

const ChevronRight = () => (
  <svg
    className="w-3.5 h-3.5 text-[#CBD5E1] flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronLeft = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const PageHeader = ({
  title,
  subtitle,
  actions,
  action,
  breadcrumbs,
  backAction,
  badge,
  bordered = true,
  className = "",
}) => {
  // `action` is accepted as an alias for `actions` because four pages already
  // passed the singular spelling. React drops an unknown prop without a word,
  // so the typo hid every header control on those pages — the year filter,
  // Refresh, View Mode — with no error anywhere to trace it by.
  const actionSlot = actions ?? action;

  return (
    <div
      className={[
        "flex flex-col gap-3 mb-6",
        // bordered ? "pb-5 border-b border-[#E2E8F0]" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Back + Title row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {/* Back button */}
          {backAction && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={backAction.onClick}
              aria-label={backAction.label ?? "Go back"}
              className="mt-0.5 flex-shrink-0"
            >
              <ChevronLeft />
            </Button>
          )}

          {/* Title block */}
          {(title || subtitle || badge) && (
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                {title && (
                  <h1 className="font-display text-2xl font-bold text-primary-500 tracking-tight leading-tight">
                    {title}
                  </h1>
                )}
                {badge && <span className="flex-shrink-0">{badge}</span>}
              </div>
              {subtitle && (
                <p className="mt-1 text-sm text-[#475569] leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action slot */}
        {actionSlot && (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end ml-auto">
            {actionSlot}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
